import React, { useState, useEffect } from 'react';
import { Modal } from 'shards-react';
import ModalEditHeader from '../../common/ModalEditHeader';
import ModalEditBody from './ModalEditBody';
import ModalEditBodyDelete from './ModalEditBodyDelete';
import ModalEditFooter from '../../../components/common/ModalEditFooter';
import { useTranslation } from 'react-i18next';
import useLocales from '../../../hooks/useLocales';
import useUserRoles from '../../../hooks/useUserRoles';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { closeDialog, createUser, updateUser, deleteUser } from '../../../redux/slices/user';
import getOrganizationUsers from '../../../utils/getOrganizationUser';
import { EDIT_ACTION, ROLE } from '../../../constants';
import type { OrganizationType } from '../../../types/organization';
import type { CreateUserParams, DeleteUserParams, UpdateUserParams, UserWithOrganizationType, OrganizationUserType } from '../../../types/user';
import type { Role, SelectOption, TradingCompanyType } from '../../../types/common';
import type { OptionsType } from '../../../types/options';

const ModalEditUser = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { currentLang } = useLocales();
  const { dialogMode, isDialogOpen, user, users } = useSelector((state: RootState) => state.user);
  const { organizationBase, tradingCompany } = useSelector((state: RootState) => state.options);
  const { organizations } = useSelector((state: RootState) => state.organization);

  const [dialogTitle, setDialogTitle] = useState<string>('');
  const [buttonLabel, setButtonLabel] = useState<string>('');
  const [deleteFlag, setDeleteFlag] = useState<boolean>(false);
  const [isUniqueEmail, setIsUniqueEmail] = useState<boolean | undefined>(undefined);

  const [selectedTradingCompany, setSelectedTradingCompany] = useState<OptionsType[]>([]);
  const [organizationUsers, setOrganizationUsers] = useState<OrganizationUserType[]>([]);
  const [optionsOrganization, setOptionsOrganization] = useState<OrganizationType[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationType[]>([]);
  const [userName, setUserName] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [userRole, setUserRole] = useState<Role[]>([]);

  const { isMaintenanceadmin } = useUserRoles();
  const isTradingCompany: boolean = selectedOrganization.some((org) => org.isTradingCompany);

  // すべて入力されているかどうか（OKボタンを押せるかどうかに使う）
  const isFulfilled: boolean = organizationUsers.length > 0 && userName !== '' && userEmail !== '' && userRole.length > 0;

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  //   console.log(organizations);
  // }, [organizations]);

  /**
   * 新規作成モード以外では初期値を入れておく
   */
  useEffect(() => {
    if (dialogMode !== EDIT_ACTION.CREATE && user && organizations && tradingCompany) {
      const organizationIds: number[] = user.organizations ? user.organizations.map((org) => org.organizationId) : [];
      const selectedOrganizations: OrganizationType[] = organizations.filter((org) => organizationIds.includes(org.id));
      setSelectedOrganization(selectedOrganizations);
      setOrganizationUsers(user.organizations);
      setUserName(user.username);
      setUserEmail(user.email);
      setUserRole(user.roles);
    }
  }, [user, organizations]);

  /**
   * モードに併せてタイトルとボタン名を設定
   */
  useEffect(() => {
    if (dialogMode === 'CREATE') {
      setDialogTitle(t('user.dialog.titleCreate'));
      setButtonLabel(t('dialog.create'));
    } else if (dialogMode === 'UPDATE') {
      setDialogTitle(t('user.dialog.titleUpdate'));
      setButtonLabel(t('dialog.update'));
    } else {
      setDialogTitle(t('user.dialog.titleDelete'));
      setButtonLabel(t('dialog.delete'));
    }
  }, [dialogMode]);

  /**
   * 商社が変わったら組織のプルダウンオプションにフィルタをかける
   */
  useEffect(() => {
    const excludeTradingCompany: OrganizationType[] = organizations ? organizations.filter((org) => !org.isTradingCompany) : [];
    const hasGeneralOrganization: boolean = selectedOrganization.some((org) => !org.isTradingCompany);
    const orgs: OrganizationType[] = hasGeneralOrganization ? excludeTradingCompany : organizations;
    if (selectedTradingCompany.length > 0) {
      const selectedTradingCompanyCodes: string[] = selectedTradingCompany.map((company) => company.code);
      const filteredOrganizations: OrganizationType[] = orgs.filter((org) => selectedTradingCompanyCodes.includes(org.tradingCompanyCode));
      setOptionsOrganization(filteredOrganizations);
    } else {
      setOptionsOrganization(orgs);
    }
  }, [selectedTradingCompany, selectedOrganization]);

  /**
   * ユーザメールアドレスの重複チェック
   */
  useEffect(() => {
    // 新規作成モードではusersに同じ名前のものがないかチェック
    if (dialogMode === EDIT_ACTION.CREATE) {
      const sameEmailUser: UserWithOrganizationType | undefined = users ? users.find((user) => user.email === userEmail) : undefined;
      if (sameEmailUser !== undefined || userEmail === '') {
        setIsUniqueEmail(false);
      } else {
        setIsUniqueEmail(true);
      }
      // 更新モードでは自分以外のusersに同じ名前のものがないかチェック
    } else if (dialogMode === EDIT_ACTION.UPDATE) {
      const sameEmailUser: UserWithOrganizationType | undefined = users.find((usr) => {
        if (usr.id !== user.id && usr.email === userEmail) {
          return true;
        } else {
          return false;
        }
      });
      if (sameEmailUser !== undefined) {
        setIsUniqueEmail(false);
      } else {
        setIsUniqueEmail(true);
      }
    } else {
      setIsUniqueEmail(undefined);
    }
  }, [userEmail, user, isDialogOpen]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * モーダルのリセット処理
   */
  const resetModal = (): void => {
    dispatch(closeDialog());
    setOrganizationUsers([]);
    setUserName('');
    setUserEmail('');
    setUserRole([]);
    setSelectedOrganization([]);
    setSelectedTradingCompany([]);
    setIsUniqueEmail(undefined);
    setDeleteFlag(false);
  };

  /**
   * 商社を変更した時
   */
  const handleTradingCompanyChange = (option: OptionsType[]): void => {
    setSelectedTradingCompany(option);
  };

  /**
   * 組織を変更した時
   */
  const handleOrganizationChange = (organizations: OrganizationType[]): void => {
    // console.log(organizations);
    if (dialogMode === EDIT_ACTION.CREATE || dialogMode === EDIT_ACTION.UPDATE) {
      const organizationUsers: OrganizationUserType[] = organizations.map((org) => {
        if (dialogMode === EDIT_ACTION.CREATE || dialogMode === EDIT_ACTION.UPDATE) {
          const organizationBaseName: string = organizationBase.find((base) => base.code === String(org.organizationBaseId))?.nameJapanese ?? '';
          const tradingCompanyOption: OptionsType | undefined = tradingCompany.find((option) => option.code === org.tradingCompanyCode);
          const organizationUser: OrganizationUserType = getOrganizationUsers(organizationBaseName, tradingCompanyOption, org, dialogMode);
          return organizationUser;
        }
      });
      // console.log(organizationUsers);
      setOrganizationUsers(organizationUsers);
      setSelectedOrganization(organizations);
    } else {
      // do nothing
    }
  };

  /**
   * ユーザ名を変更した時
   */
  const handleUserNameChange = (event: React.ChangeEvent<{ value: string }>): void => {
    // console.log(event);
    const newValue: string = event.target.value;
    setUserName(newValue);
  };

  /**
   * メールアドレスを変更した時
   */
  const handleUserEmailChange = (event: React.ChangeEvent<{ value: string }>): void => {
    // console.log(event);
    const newValue: string = event.target.value;
    setUserEmail(newValue);
  };

  /**
   * ロールを変更した時
   */
  const handleRoleChange = (roles: Role[]): void => {
    // console.log(roles);
    setUserRole(roles);
  };

  /**
   * メール通知を変更した時
   * @params organizationUser [OrganizationUserType] - メール通知フラグが反転したorganizationUser
   */
  const handleNotificationChange = (organizationUser: OrganizationUserType): void => {
    // console.log(organizationUser);
    if (dialogMode === EDIT_ACTION.CREATE || dialogMode === EDIT_ACTION.UPDATE) {
      const newOrganizationUsers: OrganizationUserType[] = organizationUsers.map((org) => {
        if (org.organizationId === organizationUser.organizationId) {
          return organizationUser;
        } else {
          return org;
        }
      });
      setOrganizationUsers(newOrganizationUsers);
    } else {
      // do nothing
    }
  };

  /**
   * モーダルでキャンセルボタンを押した時
   */
  const handleCancel = (): void => {
    resetModal();
  };

  /**
   * モーダルでOKボタンを押した時
   */
  const handleSave = (): void => {
    // 新規作成時
    if (dialogMode === EDIT_ACTION.CREATE) {
      const isMfa: boolean = userRole.includes(ROLE.USER);
      const params: CreateUserParams = {
        email: userEmail,
        roles: userRole,
        organizations: organizationUsers,
        username: userName,
        isMfa,
      };
      dispatch(createUser(params));

      // 更新時
    } else if (dialogMode === EDIT_ACTION.UPDATE && user) {
      const params: UpdateUserParams = {
        id: user.id,
        email: userEmail,
        roles: userRole,
        organizations: organizationUsers,
        username: userName,
      };
      dispatch(updateUser(params));

      // 削除時
    } else if (dialogMode === EDIT_ACTION.DELETE && user) {
      const params: DeleteUserParams = {
        id: user.id,
      };
      dispatch(deleteUser(params));
    } else {
      // do nothing
    }
    // 状態をリセット
    resetModal();
  };

  /**
   * モーダルでDeleteチェックをクリックした時
   */
  const handleToggleDelete = (): void => {
    setDeleteFlag(!deleteFlag);
  };

  // VIEW
  // ================================================================================

  return (
    <Modal id="modalEditOrganization" className="modal-dialog-scrollable" open={isDialogOpen} maxWidth={'xs'} fullWidth={true} centered={true} toggle={handleCancel}>
      <ModalEditHeader iconName={'person'} title={dialogTitle} />
      {/* 新規作成時・更新時 */}
      {(dialogMode === EDIT_ACTION.CREATE || dialogMode === EDIT_ACTION.VIEW || dialogMode === EDIT_ACTION.UPDATE) && (
        <ModalEditBody
          dialogMode={dialogMode}
          warningText={t('user.dialog.warn')}
          // 商社選択
          selectId={'selectTradingCompany'}
          selectClass={'w-100 mb-3'}
          isMulti={true}
          tradingCompany={selectedTradingCompany}
          optionsTradingCompany={tradingCompany}
          placeholder={t('filter.organizationByTradingCompany')}
          language={currentLang.value}
          onChangeSelect={handleTradingCompanyChange}
          // 組織選択
          labelOrg={t('organization.organization')}
          disabledOrg={!isMaintenanceadmin || isTradingCompany || dialogMode === EDIT_ACTION.VIEW}
          optionsOrg={optionsOrganization}
          selectedOrg={selectedOrganization}
          onChangeOrg={handleOrganizationChange}
          // ユーザ名
          labelName={t('user.name')}
          name={userName}
          disabledName={!isMaintenanceadmin || dialogMode === EDIT_ACTION.VIEW}
          onChangeName={handleUserNameChange}
          // メールアドレス
          labelEmail={t('user.email')}
          email={userEmail}
          disabledEmail={!isMaintenanceadmin || dialogMode === EDIT_ACTION.VIEW}
          onChangeEmail={handleUserEmailChange}
          // ロール
          labelRole={t('user.role')}
          disabledRole={!isMaintenanceadmin || dialogMode === EDIT_ACTION.VIEW}
          selectedRole={userRole}
          onChangeRole={handleRoleChange}
          // メール通知
          labelNotification={t('user.emailNotification')}
          disabledNotification={!isMaintenanceadmin || dialogMode === EDIT_ACTION.VIEW}
          organizationUsers={organizationUsers}
          onChangeNotification={handleNotificationChange}
        />
      )}

      {/* 削除時 */}
      {dialogMode === EDIT_ACTION.DELETE && <ModalEditBodyDelete description={t('user.dialog.card1')} label={t('user.dialog.card2')} name={userName} />}

      <ModalEditFooter
        mode={dialogMode}
        theme={dialogMode === EDIT_ACTION.DELETE ? 'danger' : 'primary'}
        disabled={
          (dialogMode === EDIT_ACTION.CREATE && !isUniqueEmail) ||
          (dialogMode === EDIT_ACTION.UPDATE && !isUniqueEmail) ||
          (dialogMode === EDIT_ACTION.DELETE && !deleteFlag) ||
          !isMaintenanceadmin ||
          !isFulfilled
        }
        labelCancel={t('dialog.cancel')}
        labelSave={buttonLabel}
        labelCheckBox={t('dialog.validDelete')}
        deleteFlag={deleteFlag}
        onCancel={handleCancel}
        onSave={handleSave}
        onToggle={handleToggleDelete}
      />
    </Modal>
  );
};

export default ModalEditUser;
